import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";
import MainLayout from "@/layouts/MainLayout";
import AuthService from "@/lib/auth";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import useAuth from "@/hooks/useAuth";

// ── Types ─────────────────────────────────────────────────────────────────────

interface CmsAlert {
  alert_id: number;
  response_id: number;
  patient_id: string;
  patient_first_name: string;
  patient_last_name: string;
  cp_id: string | null;
  cp_name: string | null;
  cp_email: string | null;
  is_care_provider: boolean;
  questionnaire_type: string;
  cms_measure_id: string;
  severity: "critical" | "warning" | "info";
  evaluation_type: string;
  computed_score: number;
  threshold_value: number;
  alert_message: string;
  required_action: string;
  cpt_billing_code: string | null;
  cms_quality_code: string | null;
  is_trend_alert: boolean;
  trend_consecutive_count: number | null;
  alert_fired_at: string;
  total_score: number;
  response_status: string;
  response_date: string;
  alert_status?: string;
  acknowledged_by?: string | null;
  acknowledged_at?: string | null;
  billed?: boolean;
  billed_at?: string | null;
}

interface CmsStats {
  active_count: number;
  active_critical: number;
  active_warning: number;
  acknowledged_count: number;
  dismissed_count: number;
  resolved_count: number;
  billed_count: number;
  unbilled_billable: number;
  unique_patients: number;
  total_alerts: number;
}

// ── Severity badge ────────────────────────────────────────────────────────────

const SeverityBadge = ({ severity }: { severity: string }) => {
  const styles: Record<string, string> = {
    critical: "bg-red-50 text-red-700 border-red-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border uppercase tracking-wider",
        styles[severity] || "bg-gray-100 text-gray-600 border-gray-200"
      )}
    >
      {severity}
    </span>
  );
};

// ── Status badge ──────────────────────────────────────────────────────────────



// ── Resolve modal ─────────────────────────────────────────────────────────────

const ResolveModal = ({
  alertId,
  onClose,
  onResolved,
}: {
  alertId: number;
  onClose: () => void;
  onResolved: () => void;
}) => {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const handleResolve = async () => {
    if (!notes.trim()) {
      toast.error("Resolution notes are required");
      return;
    }
    setLoading(true);
    const res = await AuthService.resolveCmsAlert(alertId, notes.trim());
    setLoading(false);
    if (res?.success) {
      toast.success("Alert resolved");
      onResolved();
      onClose();
    } else {
      toast.error("Failed to resolve alert");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          Resolve Alert
        </h3>
        <textarea
          className="w-full border border-gray-200 rounded-xl p-3 text-sm text-gray-700 resize-none focus:outline-none focus:border-[#1a7a4a]"
          rows={4}
          placeholder="Enter resolution notes (required)..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-xl">
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={loading}
            onClick={handleResolve}
            className="rounded-xl bg-[#1a7a4a] hover:bg-[#155e39] text-white"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resolve"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CmsAlerts() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stats, setStats] = useState<CmsStats | null>(null);
  const [alerts, setAlerts] = useState<CmsAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"active" | "mine">("active");
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [resolveModalAlertId, setResolveModalAlertId] = useState<number | null>(null);

  // Filters
  const [filterCritical, setFilterCritical] = useState(true);
  const [filterWarning, setFilterWarning] = useState(true);
  const [filterInfo, setFilterInfo] = useState(false);
  const [filterFormType, setFilterFormType] = useState<string>("");
  // Card-click filter: 'critical' | 'warning' | 'resolved' | 'unbilled' | null
  const [cardFilter, setCardFilter] = useState<"critical" | "warning" | "acknowledged" | "dismissed" | "resolved" | "unbilled" | null>(null);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await AuthService.getCmsAlertStats({ days: 30 });
      if (res) setStats(res as CmsStats);
    } catch (err) {
      console.error("Error fetching CMS stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const handleCardClick = (card: "critical" | "warning" | "acknowledged" | "dismissed" | "resolved" | "unbilled") => {
    // Toggle off if already selected
    setCardFilter(prev => prev === card ? null : card);
    setActiveTab("active");
  };

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      // Card filter overrides severity checkboxes
      if (cardFilter === "unbilled") {
        // Unbilled: active + acknowledged alerts that have a CPT code and are not yet billed
        // Pass unbilled=true so backend includes both active + acknowledged statuses
        const res = await AuthService.getActiveCmsAlerts({
          ...(filterFormType ? { questionnaire_type: filterFormType } : {}),
          ...(activeTab === "mine" && user?.id ? { cp_id: user.id } : {}),
          unbilled: "true",
          per_page: 200,
        });
        // Client-side filter: must have CPT code and not yet billed
        const results: CmsAlert[] = (res?.alerts ?? []).filter(
          (a: CmsAlert) => a.cpt_billing_code && !a.billed
        );
        setAlerts(results);
        setLoading(false);
        return;
      }
      if (cardFilter === "resolved") {
        // Resolved: use alert_status param — backend supports it now
        const res = await AuthService.getActiveCmsAlerts({
          ...(filterFormType ? { questionnaire_type: filterFormType } : {}),
          ...(activeTab === "mine" && user?.id ? { cp_id: user.id } : {}),
          alert_status: "resolved",
          per_page: 200,
        });
        setAlerts(res?.alerts ?? []);
        setLoading(false);
        return;
      }

      if (cardFilter === "acknowledged") {
        const res = await AuthService.getActiveCmsAlerts({
          ...(filterFormType ? { questionnaire_type: filterFormType } : {}),
          ...(activeTab === "mine" && user?.id ? { cp_id: user.id } : {}),
          alert_status: "acknowledged",
          per_page: 200,
        });
        setAlerts(res?.alerts ?? []);
        setLoading(false);
        return;
      }

      if (cardFilter === "dismissed") {
        const res = await AuthService.getActiveCmsAlerts({
          ...(filterFormType ? { questionnaire_type: filterFormType } : {}),
          ...(activeTab === "mine" && user?.id ? { cp_id: user.id } : {}),
          alert_status: "dismissed",
          per_page: 200,
        });
        setAlerts(res?.alerts ?? []);
        setLoading(false);
        return;
      }

      const severities: string[] = [];
      if (cardFilter === "critical") {
        severities.push("critical");
      } else if (cardFilter === "warning") {
        severities.push("warning");
      } else {
        if (filterCritical) severities.push("critical");
        if (filterWarning) severities.push("warning");
        if (filterInfo) severities.push("info");
      }

      const params: Record<string, string> = {};
      if (filterFormType) params.questionnaire_type = filterFormType;
      if (activeTab === "mine" && user?.id) params.cp_id = user.id;

      const results: CmsAlert[] = [];
      if (severities.length === 0) {
        setAlerts([]);
        setLoading(false);
        return;
      }

      for (const sev of severities) {
        const res = await AuthService.getActiveCmsAlerts({ ...params, severity: sev, per_page: 100 });
        if (res?.alerts) results.push(...(res.alerts as CmsAlert[]));
      }

      // Sort: critical first, then by date descending
      results.sort((a, b) => {
        const sevOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
        if (sevOrder[a.severity] !== sevOrder[b.severity]) return sevOrder[a.severity] - sevOrder[b.severity];
        return new Date(b.alert_fired_at).getTime() - new Date(a.alert_fired_at).getTime();
      });

      setAlerts(results);
    } catch (err) {
      console.error("Error fetching CMS alerts:", err);
    } finally {
      setLoading(false);
    }
  }, [filterCritical, filterWarning, filterInfo, filterFormType, activeTab, user?.id, cardFilter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleAcknowledge = async (alertId: number) => {
    setActionLoading(alertId);
    const res = await AuthService.acknowledgeCmsAlert(alertId);
    setActionLoading(null);
    if (res?.success) {
      toast.success("Alert acknowledged");
      fetchAlerts();
      fetchStats();
    } else {
      toast.error("Failed to acknowledge alert");
    }
  };

  const handleDismiss = async (alertId: number) => {
    setActionLoading(alertId);
    const res = await AuthService.dismissCmsAlert(alertId);
    setActionLoading(null);
    if (res?.success) {
      toast.success("Alert dismissed");
      fetchAlerts();
      fetchStats();
    } else {
      toast.error("Failed to dismiss alert");
    }
  };

  const handleBill = async (alertId: number) => {
    setActionLoading(alertId);
    const res = await AuthService.billCmsAlert(alertId);
    setActionLoading(null);
    if (res?.success) {
      toast.success("Alert marked as billed");
      fetchAlerts();
      fetchStats();
    } else {
      toast.error("Failed to bill alert");
    }
  };

  const isActionLoading = (id: number) => actionLoading === id;

  const formTypes = Array.from(new Set(alerts.map((a) => a.questionnaire_type))).sort();

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f7f8fa] p-6 lg:p-10 font-sans">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* ── Header ── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-red-500" />
                CMS Alerts
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">
                Active CMS quality alerts across your patients (last 30 days)
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchAlerts(); fetchStats(); }}
              className="rounded-xl border-gray-200 text-gray-600 hover:text-[#1a7a4a] hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>

          {/* ── Stat Cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            {statsLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse h-24" />
              ))
            ) : (
              <>
                <button
                  onClick={() => handleCardClick("critical")}
                  className={`text-left w-full rounded-2xl border p-5 shadow-sm transition-all cursor-pointer ${cardFilter === "critical" ? "border-red-400 ring-2 ring-red-200 bg-red-50" : "bg-white border-red-100 hover:border-red-300 hover:shadow-md"}`}
                >
                  <div className="text-2xl font-bold text-red-500">{stats?.active_critical ?? "—"}</div>
                  <div className="text-sm font-semibold text-gray-700 mt-1">Critical active</div>
                  <div className="text-xs text-gray-400 mt-0.5">{cardFilter === "critical" ? "▸ Filtering by critical" : "Click to filter"}</div>
                </button>
                <button
                  onClick={() => handleCardClick("warning")}
                  className={`text-left w-full rounded-2xl border p-5 shadow-sm transition-all cursor-pointer ${cardFilter === "warning" ? "border-amber-400 ring-2 ring-amber-200 bg-amber-50" : "bg-white border-amber-100 hover:border-amber-300 hover:shadow-md"}`}
                >
                  <div className="text-2xl font-bold text-amber-500">{stats?.active_warning ?? "—"}</div>
                  <div className="text-sm font-semibold text-gray-700 mt-1">Warning active</div>
                  <div className="text-xs text-gray-400 mt-0.5">{cardFilter === "warning" ? "▸ Filtering by warning" : "Click to filter"}</div>
                </button>
                <button
                  onClick={() => handleCardClick("acknowledged")}
                  className={`text-left w-full rounded-2xl border p-5 shadow-sm transition-all cursor-pointer ${cardFilter === "acknowledged" ? "border-purple-400 ring-2 ring-purple-200 bg-purple-50" : "bg-white border-purple-100 hover:border-purple-300 hover:shadow-md"}`}
                >
                  <div className="text-2xl font-bold text-purple-500">{stats?.acknowledged_count ?? "—"}</div>
                  <div className="text-sm font-semibold text-gray-700 mt-1">Acknowledged</div>
                  <div className="text-xs text-gray-400 mt-0.5">{cardFilter === "acknowledged" ? "▸ Showing acknowledged" : "Click to filter"}</div>
                </button>
                <button
                  onClick={() => handleCardClick("dismissed")}
                  className={`text-left w-full rounded-2xl border p-5 shadow-sm transition-all cursor-pointer ${cardFilter === "dismissed" ? "border-gray-400 ring-2 ring-gray-200 bg-gray-100" : "bg-white border-gray-200 hover:border-gray-400 hover:shadow-md"}`}
                >
                  <div className="text-2xl font-bold text-gray-500">{stats?.dismissed_count ?? "—"}</div>
                  <div className="text-sm font-semibold text-gray-700 mt-1">Dismissed</div>
                  <div className="text-xs text-gray-400 mt-0.5">{cardFilter === "dismissed" ? "▸ Showing dismissed" : "Click to filter"}</div>
                </button>
                <button
                  onClick={() => handleCardClick("resolved")}
                  className={`text-left w-full rounded-2xl border p-5 shadow-sm transition-all cursor-pointer ${cardFilter === "resolved" ? "border-emerald-400 ring-2 ring-emerald-200 bg-emerald-50" : "bg-white border-gray-100 hover:border-emerald-300 hover:shadow-md"}`}
                >
                  <div className="text-2xl font-bold text-emerald-600">{stats?.resolved_count ?? "—"}</div>
                  <div className="text-sm font-semibold text-gray-700 mt-1">Resolved (30d)</div>
                  <div className="text-xs text-gray-400 mt-0.5">{cardFilter === "resolved" ? "▸ Showing resolved" : `${stats?.unique_patients ?? "—"} unique patients`}</div>
                </button>
                <button
                  onClick={() => handleCardClick("unbilled")}
                  className={`text-left w-full rounded-2xl border p-5 shadow-sm transition-all cursor-pointer ${cardFilter === "unbilled" ? "border-[#1a7a4a] ring-2 ring-green-200 bg-[#e8f5ee]" : "bg-white border-gray-100 hover:border-[#1a7a4a] hover:shadow-md"}`}
                >
                  <div className="text-2xl font-bold text-[#1a7a4a]">{stats?.unbilled_billable ?? "—"}</div>
                  <div className="text-sm font-semibold text-gray-700 mt-1">Unbilled CPT</div>
                  <div className="text-xs text-gray-400 mt-0.5">{cardFilter === "unbilled" ? "▸ Showing unbilled only" : "Click to filter"}</div>
                </button>
              </>
            )}
          </div>

          {/* ── Tabs ── */}
          <div className="flex items-center bg-white p-1 rounded-2xl border border-gray-100 shadow-sm w-fit">
            <button
              onClick={() => setActiveTab("active")}
              className={cn(
                "h-9 px-5 rounded-xl text-xs font-bold transition-all",
                activeTab === "active"
                  ? "bg-[#1a7a4a] text-white shadow-md"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              All active alerts
            </button>
            <button
              onClick={() => setActiveTab("mine")}
              className={cn(
                "h-9 px-5 rounded-xl text-xs font-bold transition-all",
                activeTab === "mine"
                  ? "bg-[#1a7a4a] text-white shadow-md"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              My patients
            </button>
          </div>

          {/* ── Filters + Table ── */}
          <Card className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-visible">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-gray-700">
                  {cardFilter === "critical"     ? "Critical Alerts" :
                   cardFilter === "warning"      ? "Warning Alerts" :
                   cardFilter === "acknowledged" ? "Acknowledged Alerts" :
                   cardFilter === "dismissed"    ? "Dismissed Alerts" :
                   cardFilter === "resolved"     ? "Resolved Alerts" :
                   cardFilter === "unbilled"     ? "Unbilled CPT Alerts" :
                   activeTab === "active"        ? "Active Alerts" : "My Patients' Alerts"}
                </CardTitle>
                {cardFilter && (
                  <button
                    onClick={() => setCardFilter(null)}
                    className="text-xs text-gray-400 hover:text-gray-600 underline"
                  >
                    ✕ Clear filter
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4 mb-5">
                {/* Severity */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center space-x-2 cursor-pointer">
                    <Checkbox
                      id="f-critical"
                      checked={filterCritical}
                      onCheckedChange={(v) => setFilterCritical(Boolean(v))}
                      className="w-4 h-4 border-2 border-red-200 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500 rounded-md"
                    />
                    <label htmlFor="f-critical" className="text-sm font-bold text-red-600 cursor-pointer select-none">Critical</label>
                  </div>
                  <div className="flex items-center space-x-2 cursor-pointer">
                    <Checkbox
                      id="f-warning"
                      checked={filterWarning}
                      onCheckedChange={(v) => setFilterWarning(Boolean(v))}
                      className="w-4 h-4 border-2 border-amber-200 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500 rounded-md"
                    />
                    <label htmlFor="f-warning" className="text-sm font-bold text-amber-600 cursor-pointer select-none">Warning</label>
                  </div>
                  <div className="flex items-center space-x-2 cursor-pointer">
                    <Checkbox
                      id="f-info"
                      checked={filterInfo}
                      onCheckedChange={(v) => setFilterInfo(Boolean(v))}
                      className="w-4 h-4 border-2 border-blue-200 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 rounded-md"
                    />
                    <label htmlFor="f-info" className="text-sm font-bold text-blue-600 cursor-pointer select-none">Info</label>
                  </div>
                </div>

                <div className="w-px h-6 bg-gray-100 hidden lg:block" />

                {/* Form type */}
                <select
                  value={filterFormType}
                  onChange={(e) => setFilterFormType(e.target.value)}
                  className="h-9 px-3 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 bg-white focus:outline-none focus:border-[#1a7a4a]"
                >
                  <option value="">All forms</option>
                  {formTypes.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              {/* Table */}
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-6 w-6 animate-spin text-[#1a7a4a]" />
                </div>
              ) : alerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                  <CheckCircle className="h-8 w-8 mb-2 text-emerald-300" />
                  <p className="text-sm font-medium">No active alerts matching your filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr>
                        {["Patient", "Form", "CMS Measure", "Severity", "Score / Threshold", "Alert", "Fired", "Provider", "Actions"].map((h) => (
                          <th
                            key={h}
                            className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100 whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {alerts.map((alert) => (
                        <tr
                          key={alert.alert_id}
                          className={cn(
                            "hover:bg-gray-50 transition-colors border-b border-gray-50",
                            alert.severity === "critical" && "bg-red-50/30"
                          )}
                        >
                          <td className="px-3 py-3">
                            <button
                              onClick={() => navigate(`/patients/${alert.patient_id}`)}
                              className="font-semibold text-[#1a7a4a] hover:text-[#15633c] hover:underline text-left"
                            >
                              {alert.patient_first_name} {alert.patient_last_name}
                            </button>
                          </td>
                          <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{alert.questionnaire_type}</td>
                          <td className="px-3 py-3 text-gray-400 text-xs">{alert.cms_measure_id}</td>
                          <td className="px-3 py-3">
                            <SeverityBadge severity={alert.severity} />
                          </td>
                          <td className="px-3 py-3 font-mono text-sm">
                            <span className={cn(
                              "font-bold",
                              alert.severity === "critical" ? "text-red-600" :
                              alert.severity === "warning" ? "text-amber-600" : "text-blue-600"
                            )}>
                              {alert.computed_score}
                            </span>
                            <span className="text-gray-400"> / {alert.threshold_value}</span>
                            {alert.is_trend_alert && (
                              <span className="ml-1 text-xs text-purple-500 font-medium">
                                (trend ×{alert.trend_consecutive_count})
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-gray-600 text-xs max-w-[200px]">
                            <div className="truncate" title={alert.alert_message}>{alert.alert_message}</div>
                            <div className="text-gray-400 truncate mt-0.5" title={alert.required_action}>{alert.required_action}</div>
                          </td>
                          <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">
                            {formatDate(alert.alert_fired_at, "MM/dd/yyyy")}
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-500 max-w-[120px]">
                            <div className="truncate" title={alert.cp_name ?? "—"}>{alert.cp_name ?? <span className="text-gray-300">—</span>}</div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {isActionLoading(alert.alert_id) ? (
                                <Loader2 className="h-4 w-4 animate-spin text-[#1a7a4a]" />
                              ) : (
                                <>
                                  {/* Acknowledge — disabled once already acknowledged/resolved/dismissed */}
                                  <button
                                    onClick={() => handleAcknowledge(alert.alert_id)}
                                    title={alert.alert_status !== "active" ? `Already ${alert.alert_status}` : "Acknowledge"}
                                    disabled={alert.alert_status !== "active"}
                                    className={`p-1.5 rounded-lg border transition-colors ${
                                      alert.alert_status !== "active"
                                        ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed opacity-50"
                                        : "border-gray-200 hover:border-purple-300 hover:bg-purple-50 text-gray-400 hover:text-purple-600"
                                    }`}
                                  >
                                    <Clock className="h-3.5 w-3.5" />
                                  </button>
                                  {/* Resolve — disabled once resolved/dismissed */}
                                  <button
                                    onClick={() => setResolveModalAlertId(alert.alert_id)}
                                    title={alert.alert_status === "resolved" ? "Already resolved" : alert.alert_status === "dismissed" ? "Already dismissed" : "Resolve"}
                                    disabled={alert.alert_status === "resolved" || alert.alert_status === "dismissed"}
                                    className={`p-1.5 rounded-lg border transition-colors ${
                                      alert.alert_status === "resolved" || alert.alert_status === "dismissed"
                                        ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed opacity-50"
                                        : "border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-400 hover:text-emerald-600"
                                    }`}
                                  >
                                    <CheckCircle className="h-3.5 w-3.5" />
                                  </button>
                                  {/* Dismiss — disabled once resolved/dismissed */}
                                  <button
                                    onClick={() => handleDismiss(alert.alert_id)}
                                    title={alert.alert_status === "dismissed" ? "Already dismissed" : alert.alert_status === "resolved" ? "Already resolved" : "Dismiss"}
                                    disabled={alert.alert_status === "resolved" || alert.alert_status === "dismissed"}
                                    className={`p-1.5 rounded-lg border transition-colors ${
                                      alert.alert_status === "resolved" || alert.alert_status === "dismissed"
                                        ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed opacity-50"
                                        : "border-gray-200 hover:border-gray-400 hover:bg-gray-50 text-gray-400 hover:text-gray-600"
                                    }`}
                                  >
                                    <XCircle className="h-3.5 w-3.5" />
                                  </button>
                                  {/* Bill — only show if CPT code exists, disabled once billed */}
                                  {alert.cpt_billing_code && (
                                    <button
                                      onClick={() => handleBill(alert.alert_id)}
                                      title={alert.billed ? `Already billed — ${alert.cpt_billing_code}` : `Bill — ${alert.cpt_billing_code}`}
                                      disabled={!!alert.billed}
                                      className={`px-2 py-1 rounded-lg border transition-colors text-xs font-semibold ${
                                        alert.billed
                                          ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed opacity-50 line-through"
                                          : "border-gray-200 hover:border-[#1a7a4a] hover:bg-[#e8f5ee] text-gray-400 hover:text-[#1a7a4a]"
                                      }`}
                                    >
                                      {alert.billed ? "Billed" : "Bill"}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Resolve modal */}
      {resolveModalAlertId !== null && (
        <ResolveModal
          alertId={resolveModalAlertId}
          onClose={() => setResolveModalAlertId(null)}
          onResolved={() => { fetchAlerts(); fetchStats(); }}
        />
      )}
    </MainLayout>
  );
}
